import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { webAlert, webConfirm } from "../webDialog";
import { useAuthStore } from "../../state/authStore";
import { Icon } from "../components/Icon";
import {
  fetchEntityImageObjectUrl,
  VideoEntity,
  videoEntitiesApi,
} from "../../api/videoEntities";

// ─────────────────────────────────────────────────────────────────────────
// WebEntityManager — modal that lets the parent build a per-video library
// of "objects" (e.g. "Moon", "Cat", "Cookies"). The objects show up in the
// question editor's option picker so a question can ask "which one is the
// moon?" with an actual image of the moon as a choice instead of plain
// text.
//
// Until Wan's entity-extraction endpoint exists, every entity is hand-
// uploaded by the parent: <input type=file> → FileReader → data URL →
// POST /v1/videos/:videoId/entities. The backend caps each upload at 2MB,
// PNG/JPEG/WebP, and stores bytes directly in Postgres.
// ─────────────────────────────────────────────────────────────────────────

type Props = {
  videoId: string;
  videoTitle?: string;
  onClose: () => void;
  // Called after every successful upload/delete so callers (the question
  // editor especially) can refresh their picker. Optional.
  onChange?: (entities: VideoEntity[]) => void;
};

export function WebEntityManager({
  videoId,
  videoTitle,
  onClose,
  onChange,
}: Props) {
  const { accessToken } = useAuthStore();
  const [entities, setEntities] = useState<VideoEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftDataUrl, setDraftDataUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    videoEntitiesApi
      .list(videoId, accessToken)
      .then((rows) => {
        if (cancelled) return;
        setEntities(rows);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof Error ? e.message : "Couldn't load objects.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, videoId]);

  // Notify callers whenever the entity list changes — lets the question
  // editor live-refresh its picker without a manual re-open.
  useEffect(() => {
    onChange?.(entities);
  }, [entities, onChange]);

  const onPickFile = useCallback(() => fileRef.current?.click(), []);

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Read as data URL so we can both preview and ship to the backend
      // (which expects the data:image/...;base64,... shape).
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result ?? "");
        if (!result.startsWith("data:image/")) {
          setUploadError("Please pick a PNG, JPEG, or WebP image.");
          return;
        }
        setDraftDataUrl(result);
        // Default the label to the filename without the extension — saves
        // a click in the common case. Parent can still override before
        // saving.
        if (!draftLabel.trim()) {
          const base = file.name.replace(/\.[^.]+$/, "");
          setDraftLabel(base);
        }
        setUploadError(null);
      };
      reader.onerror = () => setUploadError("Couldn't read that file.");
      reader.readAsDataURL(file);
      // Reset the file input so the same file can be picked twice in a row.
      if (fileRef.current) fileRef.current.value = "";
    },
    [draftLabel],
  );

  const onUpload = useCallback(async () => {
    if (!accessToken) return;
    const label = draftLabel.trim();
    if (!label) {
      setUploadError("Give the object a name (e.g. \"Moon\").");
      return;
    }
    if (!draftDataUrl) {
      setUploadError("Pick an image first.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const created = await videoEntitiesApi.create(
        videoId,
        { label, dataUrl: draftDataUrl },
        accessToken,
      );
      setEntities((prev) => [...prev, created]);
      setDraftLabel("");
      setDraftDataUrl(null);
    } catch (e) {
      setUploadError(
        e instanceof Error ? e.message : "Couldn't upload that object.",
      );
    } finally {
      setUploading(false);
    }
  }, [accessToken, draftLabel, draftDataUrl, videoId]);

  const onDelete = useCallback(
    async (entityId: string) => {
      if (!accessToken) return;
      const ok = await webConfirm("Remove this object? Questions that use it will lose the image.", { confirmLabel: "Confirm", destructive: true });
      if (!ok) return;
      try {
        await videoEntitiesApi.remove(entityId, accessToken);
        setEntities((prev) => prev.filter((e) => e.id !== entityId));
      } catch (e) {
        webAlert(
          e instanceof Error ? e.message : "Couldn't delete object.",
        );
      }
    },
    [accessToken],
  );

  return (
    <div className="ws-modal" onClick={onClose}>
      <div
        className="ws-modal-card we-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="ws-modal-close" onClick={onClose}>
          ×
        </button>
        <div className="we-head">
          <div className="we-eyebrow">OBJECTS</div>
          <div className="we-title">{videoTitle ?? "Custom cartoon"}</div>
          <div className="we-sub">
            Upload pictures of the things in your cartoon. They become
            picture-answers in the question editor.
          </div>
        </div>

        {loading ? (
          <div className="we-loading">Loading…</div>
        ) : loadError ? (
          <div className="we-error">{loadError}</div>
        ) : (
          <>
            {entities.length === 0 ? (
              <div className="we-empty">
                No objects yet. Upload your first one below.
              </div>
            ) : (
              <div className="we-grid">
                {entities.map((ent) => (
                  <EntityTile
                    key={ent.id}
                    entity={ent}
                    onDelete={() => onDelete(ent.id)}
                  />
                ))}
              </div>
            )}

            <div className="we-uploader">
              <div className="we-up-grid">
                <div className="we-up-preview">
                  {draftDataUrl ? (
                    <img src={draftDataUrl} alt="" />
                  ) : (
                    <button
                      className="we-up-empty"
                      onClick={onPickFile}
                      type="button"
                    >
                      <Icon name="plus" size={22} />
                      <span>Pick an image</span>
                    </button>
                  )}
                </div>
                <div className="we-up-form">
                  <label className="we-field">
                    <span className="we-field-label">Name</span>
                    <input
                      className="w-input"
                      placeholder='e.g. "Moon", "Cat"'
                      value={draftLabel}
                      onChange={(e) => setDraftLabel(e.target.value)}
                    />
                  </label>
                  <div className="we-up-actions">
                    <button
                      className="btn btn-ghost"
                      onClick={onPickFile}
                      type="button"
                    >
                      {draftDataUrl ? "Replace image" : "Pick image"}
                    </button>
                    <button
                      className="btn"
                      onClick={onUpload}
                      disabled={uploading || !draftDataUrl || !draftLabel.trim()}
                    >
                      {uploading ? "Uploading…" : "Add object"}
                    </button>
                  </div>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={onFileChange}
              />
              {uploadError && <div className="we-error">{uploadError}</div>}
            </div>
          </>
        )}
      </div>

      <WEStyles />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// One tile in the grid. Fetches its image via the auth'd helper and
// converts to an object URL so <img src> works without leaking the JWT.
// Revokes on unmount.
// ─────────────────────────────────────────────────────────────────────────
function EntityTile({
  entity,
  onDelete,
}: {
  entity: VideoEntity;
  onDelete: () => void;
}) {
  const { accessToken } = useAuthStore();
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let revoked = false;
    let current: string | null = null;
    fetchEntityImageObjectUrl(entity, accessToken)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        current = url;
        setObjUrl(url);
      })
      .catch((e: unknown) => {
        setErr(e instanceof Error ? e.message : "Failed to load");
      });
    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [accessToken, entity]);

  return (
    <div className="we-tile">
      <div className="we-tile-img">
        {objUrl ? (
          <img src={objUrl} alt={entity.label} />
        ) : err ? (
          <div className="we-tile-err">!</div>
        ) : (
          <div className="we-tile-spin" />
        )}
      </div>
      <div className="we-tile-label">{entity.label}</div>
      <button
        className="we-tile-del"
        onClick={onDelete}
        title="Remove object"
        type="button"
      >
        ×
      </button>
    </div>
  );
}

// Memoized image renderer used by the question option picker. Same auth-
// safe object URL pattern as `EntityTile` but as a standalone component
// callers can drop into their own layouts.
export function EntityImage({
  entity,
  alt,
  className,
  style,
}: {
  entity: Pick<VideoEntity, "id" | "imageUrl" | "label">;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { accessToken } = useAuthStore();
  const [objUrl, setObjUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!accessToken) return;
    let revoked = false;
    let current: string | null = null;
    fetchEntityImageObjectUrl(entity as VideoEntity, accessToken)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        current = url;
        setObjUrl(url);
      })
      .catch(() => {});
    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [accessToken, entity]);
  if (!objUrl) {
    return (
      <div
        className={className}
        style={{
          background: "var(--bg-warm)",
          ...style,
        }}
      />
    );
  }
  return (
    <img src={objUrl} alt={alt ?? entity.label} className={className} style={style} />
  );
}

// Hook-style helper to keep an entity-list in sync. Used by the question
// editor so its option picker always shows the latest objects.
export function useVideoEntities(videoId: string | null): {
  entities: VideoEntity[];
  refresh: () => Promise<void>;
  loading: boolean;
} {
  const { accessToken } = useAuthStore();
  const [entities, setEntities] = useState<VideoEntity[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!videoId || !accessToken) return;
    setLoading(true);
    try {
      const rows = await videoEntitiesApi.list(videoId, accessToken);
      setEntities(rows);
    } catch {
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [videoId, accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(
    () => ({ entities, refresh, loading }),
    [entities, refresh, loading],
  );
}

function WEStyles() {
  return (
    <style>{`
      .we-card {
        width: min(720px, 100%);
        max-height: calc(100vh - 48px);
        overflow-y: auto;
        padding: 26px 26px 22px;
      }
      .we-head { margin-bottom: 16px; }
      .we-eyebrow {
        font-size: 11px; font-weight: 900; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--primary-ink);
      }
      .we-title {
        font-family: var(--display);
        font-size: 24px; font-weight: 900;
        color: var(--ink); margin-top: 4px;
      }
      .we-sub {
        font-size: 13px; font-weight: 600;
        color: var(--ink-2); margin-top: 4px;
      }
      .we-loading, .we-empty {
        padding: 24px;
        text-align: center;
        color: var(--ink-3);
        font-weight: 700;
        border: 1.5px dashed var(--line);
        border-radius: 12px;
      }
      .we-error {
        font-size: 12.5px; font-weight: 700;
        color: #C0392B;
        background: rgba(192,57,43,0.08);
        padding: 8px 10px;
        border-radius: 8px;
        margin-top: 10px;
      }
      .we-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
        gap: 10px;
        margin-bottom: 18px;
      }
      .we-tile {
        position: relative;
        border: 1.5px solid var(--line);
        border-radius: 12px;
        overflow: hidden;
        background: var(--surface);
      }
      .we-tile-img {
        aspect-ratio: 1 / 1;
        background: var(--bg-warm);
        display: grid; place-items: center;
        overflow: hidden;
      }
      .we-tile-img img {
        width: 100%; height: 100%;
        object-fit: cover;
        display: block;
      }
      .we-tile-spin {
        width: 18px; height: 18px;
        border: 2.5px solid rgba(180,90,36,0.18);
        border-top-color: var(--primary);
        border-radius: 50%;
        animation: we-spin 700ms linear infinite;
      }
      @keyframes we-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      .we-tile-err {
        font-weight: 900;
        color: #C44;
      }
      .we-tile-label {
        font-size: 12px; font-weight: 800;
        color: var(--ink);
        padding: 6px 8px;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .we-tile-del {
        position: absolute; top: 4px; right: 4px;
        width: 22px; height: 22px;
        border: 0; border-radius: 50%;
        background: rgba(0,0,0,0.55);
        color: #fff; cursor: pointer;
        font-weight: 900; font-size: 14px;
        line-height: 1;
      }

      .we-uploader {
        border: 1.5px solid var(--primary);
        background: var(--bg-warm);
        border-radius: 14px;
        padding: 14px;
      }
      .we-up-grid {
        display: grid;
        grid-template-columns: 140px 1fr;
        gap: 14px;
        align-items: center;
      }
      .we-up-preview {
        aspect-ratio: 1 / 1;
        border-radius: 10px;
        overflow: hidden;
        background: var(--surface);
        display: grid; place-items: center;
      }
      .we-up-preview img {
        width: 100%; height: 100%; object-fit: cover;
      }
      .we-up-empty {
        width: 100%; height: 100%;
        background: transparent; border: 0;
        cursor: pointer;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 6px;
        color: var(--ink-3);
        font: inherit; font-weight: 700; font-size: 12px;
      }
      .we-up-form { display: flex; flex-direction: column; gap: 10px; }
      .we-field { display: flex; flex-direction: column; gap: 4px; }
      .we-field-label {
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--ink-3);
      }
      .we-up-actions {
        display: flex; gap: 8px;
      }
    `}</style>
  );
}

export default WebEntityManager;
